import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Form,
  Input,
  Select,
  Radio,
  Button,
  Alert,
  Spin,
  DatePicker,
  Card,
  Typography,
  Space,
} from "antd";
import API_BASE_URL from "../../config/api";
import { ELEVENLABS_CONFIG } from "../../config/elevenlabs-api";

const { Option } = Select;
const { Title } = Typography;

export const BatchCallForm = ({ batchCall, onClose, onSubmit }) => {
  const [form] = Form.useForm();
  const [agents, setAgents] = useState([]);
  const [contactBunches, setContactBunches] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/contacts`, {
        headers: { Authorization: token },
      });
      const contactsData = response.data;
      // Ensure contacts is always an array
      const contactsArray = Array.isArray(contactsData)
        ? contactsData
        : contactsData.contacts || contactsData.data || [];
      setContacts(contactsArray);
    } catch (err) {
      console.error("Failed to fetch contacts:", err);
      setContacts([]);
    }
  };

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/agents`, {
          headers: { Authorization: token },
        });

        // Handle different response structures
        let agentsData = response.data;
        if (response.data && response.data.data) {
          agentsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          agentsData = response.data;
        } else {
          agentsData = [];
        }

        setAgents(agentsData);
      } catch (err) {
        setError("Failed to fetch agents");
        setAgents([]);
      }
    };
    const fetchContactBunches = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/contact-bunches`, {
          headers: { Authorization: token },
        });
        const bunchesData = response.data;
        // Ensure contactBunches is always an array
        const bunchesArray = Array.isArray(bunchesData)
          ? bunchesData
          : bunchesData.contactBunches || bunchesData.data || [];
        setContactBunches(bunchesArray);
      } catch (err) {
        console.error("Failed to fetch contact bunches:", err);
        setContactBunches([]);
      }
    };
    fetchAgents();
    fetchContactBunches();
    fetchContacts();
    if (batchCall) {
      form.setFieldsValue({
        campaignName: batchCall.batch_name || "",
        agentSelect: batchCall.agent_id || "",
        scheduledTime: batchCall.scheduled_time
          ? new Date(batchCall.scheduled_time)
          : null,
        recipientType: "contacts",
      });
    }
  }, [batchCall, form]);

  const fetchContactsByBunches = async (bunchIds) => {
    const token = localStorage.getItem("token");
    const contacts = [];

    for (const id of bunchIds) {
      const response = await axios.get(
        `${API_BASE_URL}/contact-bunches/${id}`,
        {
          headers: { Authorization: token },
        }
      );
      contacts.push(...response.data.contacts);
    }

    return contacts;
  };

  const handleSubmit = async (values) => {
    setError("");
    setSuccessMessage("");
    setLoading(true);
    let recipientsArray = [];

    // Fetch existing batch calls to check for duplicate names
    try {
      const token = localStorage.getItem("token");
      const existingBatchCallsResponse = await fetch(
        `${API_BASE_URL}/batch-calls`,
        {
          headers: { Authorization: token },
        }
      );
      const existingBatchCalls = await existingBatchCallsResponse.json();

      const duplicate = existingBatchCalls.find(
        (call) =>
          call.name.toLowerCase() ===
            values.campaignName.trim().toLowerCase() &&
          (!batchCall || call.id !== batchCall.id) // exclude current batchCall if editing
      );

      if (duplicate) {
        setError(
          "A batch call campaign with this name already exists. Please choose a different name."
        );
        setLoading(false);
        return;
      }
    } catch (fetchError) {
      console.error(
        "Failed to fetch existing batch calls for duplicate check:",
        fetchError
      );
      // Proceed without blocking submission if fetch fails
    }

    if (values.recipientType === "contacts") {
      if (!values.contactsSelect || values.contactsSelect.length === 0) {
        setError("Recipients are required");
        setLoading(false);
        return;
      }
      recipientsArray = contacts
        .filter((contact) =>
          values.contactsSelect.includes(contact.id.toString())
        )
        .map((contact) => ({
          phone_number: contact.phone,
          conversation_initiation_client_data: {
            dynamic_variables: {
              call_agent_id: values.agentSelect,
              call_user_phone: contact.phone,
              call_user_id: contact.id.toString(),
              call_user_name: contact.first_name || "Valued Customer",
            },
          },
        }));
    } else if (values.recipientType === "bunches") {
      if (!values.bunchesSelect || values.bunchesSelect.length === 0) {
        setError("Recipients are required");
        setLoading(false);
        return;
      }
      const bunchContacts = await fetchContactsByBunches(values.bunchesSelect);
      recipientsArray = bunchContacts.map((contact) => ({
        phone_number: contact.phone,

        conversation_initiation_client_data: {
          dynamic_variables: {
            call_agent_id: values.agentSelect,
            call_user_phone: contact.phone,
            call_user_id: contact.id.toString(),
            call_user_name: contact.first_name || "Valued Customer",
          },
        },
      }));

      if (recipientsArray.length === 0) {
        setError("No contacts found in selected bunches");
        setLoading(false);
        return;
      }
    }

    const scheduledUnix = values.scheduledTime
      ? Math.floor(new Date(values.scheduledTime).getTime() / 1000)
      : 0;

    try {
      await fetchContacts();

      await fetch(
        `${ELEVENLABS_CONFIG.BASE_URL}/batch-calling/submit`,
        {
          method: "POST",
          headers: {
            "xi-api-key": ELEVENLABS_CONFIG.API_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            call_name: values.campaignName,
            agent_id: values.agentSelect,
            agent_phone_number_id: ELEVENLABS_CONFIG.AGENT_PHONE_NUMBER_ID,
            scheduled_time_unix: scheduledUnix,
            recipients: recipientsArray,
          }),
        }
      );

      setSuccessMessage("Batch call campaign created successfully");
      onSubmit();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "0" }}>
      {error && (
        <Alert message={error} type="error" showIcon className="mb-4" />
      )}
      {successMessage && (
        <Alert
          message={successMessage}
          type="success"
          showIcon
          className="mb-4"
        />
      )}
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <Form.Item
          name="campaignName"
          label="Campaign Name"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="agentSelect"
          label="Agent Selection"
          rules={[{ required: true }]}
        >
          <Select placeholder="Select an Agent">
            {Array.isArray(agents) &&
              agents.map((agent) => (
                <Option
                  key={agent.id}
                  value={agent.elevenlabs_agent_id || agent.id}
                >
                  {agent.name}
                </Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item name="scheduledTime" label="Scheduled Time (Optional)">
          <DatePicker showTime />
        </Form.Item>
        <Form.Item
          name="recipientType"
          label="Select Recipients Type"
          rules={[{ required: true }]}
        >
          <Radio.Group>
            <Radio value="contacts">Select Individual Contacts</Radio>
            <Radio value="bunches">Select Contact Bunches</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) =>
            prev.recipientType !== curr.recipientType
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("recipientType") === "contacts" ? (
              <Form.Item
                name="contactsSelect"
                label="Select Contacts"
                rules={[{ required: true }]}
              >
                <Select mode="multiple" placeholder="Select Contacts">
                  {Array.isArray(contacts) &&
                    contacts.map((contact) => (
                      <Option key={contact.id} value={contact.id.toString()}>
                        {contact.name} {contact.phone && `(${contact.phone})`}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            ) : (
              <Form.Item
                name="bunchesSelect"
                label="Select Contact Bunches"
                rules={[{ required: true }]}
              >
                <Select mode="multiple" placeholder="Select Contact Bunches">
                  {contactBunches.map((bunch) => (
                    <Option key={bunch.id} value={bunch.id.toString()}>
                      {bunch.name} ({bunch.contact_count || 0} contacts)
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )
          }
        </Form.Item>
        <Form.Item>
          <Form.Item style={{ textAlign: "right" }}>
            <Space>
              <Button type="default" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {batchCall ? "Update Campaign" : "Create Campaign"}
              </Button>
            </Space>
          </Form.Item>
        </Form.Item>
      </Form>
    </div>
  );
};
